import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GlobalServiceService } from 'src/app/services/global/global-service.service';
import { finalize, Subscription, take } from 'rxjs';
import { PageLoaderService } from 'src/app/services/page-loader.service';
import { WalletService } from 'src/generated-sources/openapi/wallet-service/wallet';
import {
  PortfolioManagerComponent,
  IWalletDialogResponse,
} from 'src/app/components/entity-manager/portfolio-manager/portfolio-manager.component';
import { CommonModule } from '@angular/common';
import { PersonType } from 'src/app/services/models/enums';
import { MatButtonModule } from '@angular/material/button';
import { LocalStorageService } from 'src/app/services/storage/local-storage.service';
import { WalletDataService } from 'src/app/services/data/wallet-data.service';
import { WalletAttributesAlertConfigDialogComponent } from 'src/app/components/wallet-alerts/wallet-attributes-alert-config-dialog/wallet-attributes-alert-config-dialog.component';
import {
  ConfirmationDialogComponent,
  ConfirmDialogResult,
  ConfirmDialogResultType,
} from 'src/app/shared/components/modals/confirmation-dialog/confirmation-dialog.component';
import { GenericTableComponent } from 'src/app/shared/components/generic-table/generic-table.component';
import { TableCellDirective } from 'src/app/shared/components/generic-table/directives/table-cell.directive';
import {
  getWalletFavoriteTableConfig,
  WALLET_FAVORITE_NO_DATA_MESSAGE,
} from './wallet-favorite-table-config';
import { Wallet } from 'src/app/services/models/wallet-service/wallet.model';
import { isAlert30DaysWallet } from 'src/app/helpers/plan-utils';

@Component({
  selector: 'app-wallet-favorite-list',
  templateUrl: './wallet-favorite-list.component.html',
  styleUrls: ['./wallet-favorite-list.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatTooltipModule,
    GenericTableComponent,
    TableCellDirective,
  ],
})
export class WalletFavoriteListComponent implements OnInit, OnDestroy {
  @Output()
  modelSendToRedirect = new EventEmitter();

  totalRegisters: number = 0;
  perPage: number = 10;
  page: number = 0;
  isLoading: boolean = false;

  favorites: (Wallet & { isAlert30Days?: boolean })[] = [];
  personType = PersonType.COMPANY;
  noDataMessage = WALLET_FAVORITE_NO_DATA_MESSAGE;

  tableConfig = getWalletFavoriteTableConfig();

  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly dialog: MatDialog,
    private readonly _snackBar: MatSnackBar,
    private readonly walletService: WalletService,
    private readonly walletDataService: WalletDataService,
    private readonly globalService: GlobalServiceService,
    private readonly localStorageService: LocalStorageService,
    private readonly pageLoaderService: PageLoaderService,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.globalService.getPersonType$().subscribe((personType) => {
        this.personType = personType;
        this.loadMoreData();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  get urlAssets() {
    return this.localStorageService.getItem('urlAssets');
  }

  handleDelete(walletToDelete: Wallet) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        minWidth: '300px',
        data: {
          title: $localize`:@@WalletDeletionTitle:Eliminación de cartera`,
          message: $localize`:@@ConfirmWalletDeletion:¿Confirmar la eliminación de la cartera "${walletToDelete.name}"?`,
          confirmButton: {
            text: $localize`:@@js_Delete:Eliminar`,
            style: 'danger',
            icon: 'delete_outlined',
          },
        },
      })
      .afterClosed()
      .subscribe((close: ConfirmDialogResultType) => {
        if (close === ConfirmDialogResult.Confirm) {
          this.pageLoaderService.setPageLoader(true);

          this.walletService
            .deleteById({ id: walletToDelete.id })
            .pipe(finalize(() => this.pageLoaderService.setPageLoader(false)))
            .subscribe({
              next: () => {
                this.openSnackBar(
                  $localize`:@@wasDeletedCorrectly:Se eliminó correctamente: ${walletToDelete.name}`,
                );
                this.walletDataService.deleteWallet(walletToDelete.id);
                this.loadMoreData();
              },
              error: ({ error }) => {
                const defaultExclusionErrorMessage = $localize`:@@ErrorTryingToExclude:Error al intentar eliminar`;
                const responseErrorMessage = error?.errors?.[0]?.message ?? error?.message;
                this.openSnackBarError(responseErrorMessage ?? defaultExclusionErrorMessage);
              },
            });
        }
      });
  }

  handleEdit(walletToEdit: Wallet) {
    if (isAlert30DaysWallet((walletToEdit as any).planId)) {
      return;
    }
    this.pageLoaderService.setPageLoader(true);
    this.walletService
      .findById({ id: walletToEdit.id })
      .pipe(finalize(() => this.pageLoaderService.setPageLoader(false)))
      .subscribe({
        next: (wallet) => {
          this.openWalletEditDialog(wallet);
        },
      });
  }

  openWalletEditDialog(walletToEdit: Wallet) {
    const dialogRef = this.dialog.open(PortfolioManagerComponent, {
      data: walletToEdit,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((value: IWalletDialogResponse | undefined) => {
        const walletWasUpdated = value?.data;
        if (!walletWasUpdated) return;

        const editedWallet: Wallet = value.data;

        const favorite = this.favorites.find((wallet) => wallet.id === editedWallet.id);
        const toggledFavorite = editedWallet.favorite !== favorite?.favorite;

        if (favorite) {
          favorite.name = editedWallet.name;
        }

        this.walletDataService.updateWallet(editedWallet);

        if (toggledFavorite) {
          this.loadMoreData();
        }
      });
  }

  toggleFavorite(walletToToggle: Wallet) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        minWidth: '300px',
        data: {
          title: $localize`:@@favoriteRemovalTitle:Eliminación de favorito`,
          message: $localize`:@@confirmWalletFavoriteRemoval:¿Confirmar la eliminación de la cartera "${walletToToggle.name}" de la lista de favoritos?`,
          confirmButton: {
            text: $localize`:@@js_Remove:Eliminar`,
            style: 'danger',
            icon: 'delete_outlined',
          },
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((close: ConfirmDialogResultType) => {
        if (close === ConfirmDialogResult.Confirm) {
          this.pageLoaderService.setPageLoader(true);

          this.walletService
            .toggleFavorite({ id: walletToToggle.id })
            .pipe(finalize(() => this.pageLoaderService.setPageLoader(false)))
            .subscribe({
              next: () => {
                walletToToggle.favorite = !walletToToggle.favorite;
                this.loadMoreData();
              },
            });
        }
      });
  }

  manageAlerts(wallet: Wallet) {
    this.dialog.open(WalletAttributesAlertConfigDialogComponent, {
      data: wallet,
      width: '100%',
      maxWidth: '900px',
      height: '600px',
    });
  }

  getFavoriteIcon() {
    return `${this.urlAssets}/icons/carteras-select-human.png`;
  }

  changePage(page = 0) {
    this.page = page;
    this.loadMoreData();
  }

  selectedOptionNavbarRedirect(wallet: Wallet) {
    this.modelSendToRedirect.emit(wallet);
  }

  loadMoreData() {
    this.isLoading = true;
    this.walletService.listFavoritePageable({ page: this.page, perPage: this.perPage }).subscribe({
      next: (response) => {
        this.favorites = response.items.map((wallet) => ({
          ...wallet,
          isAlert30Days: isAlert30DaysWallet((wallet as any).planId),
        }));
        this.totalRegisters = response.total;
        this.isLoading = false;
      },
      error: () => {
        this.favorites = [];
        this.totalRegisters = 0;
        this.isLoading = false;
      },
    });
  }

  openSnackBar(message: string) {
    this._snackBar.open(message, $localize`:@@js_Close:Cerrar`, {
      duration: 7000,
    });
  }

  openSnackBarError(message: string) {
    this._snackBar.open(message, $localize`:@@js_Close:Cerrar`, {
      duration: 7000,
      panelClass: ['snackbar-fail'],
    });
  }
}
